import {useEffect, useRef} from "react";
import axios from "axios";

/**
 * Pollt einen URL im Rhythmus intervalMs - aber nur, solange der Tab
 * sichtbar ist (document.visibilityState === "visible"). Bei Hintergrund-
 * Tabs bleibt das Polling pausiert: sonst würden zehn offene Tabs ein
 * fremdes System (hier das Feedback-Portal) sinnlos weiter belasten.
 *
 * Bei Rückkehr in den Vordergrund wird sofort einmal abgefragt, damit nach
 * längerer Pause nicht erst der nächste Intervall-Tick abgewartet werden
 * muss. Netzwerkfehler werden still ignoriert: ein Ausfall des Zielsystems
 * darf das Polling weder stoppen noch die Konsole zumüllen - beim nächsten
 * Tick wird es erneut versucht.
 *
 * onData erhält bei jedem erfolgreichen Abruf die Antwort. Ist url null,
 * läuft gar kein Polling (so lässt sich der Hook bedingt aktivieren, ohne
 * gegen die Regel der bedingten Hook-Aufrufe zu verstoßen).
 *
 * @param {string|null} url        URL zum Pollen; null deaktiviert das Polling.
 * @param {number} intervalMs      Takt in Millisekunden (nur im sichtbaren Tab).
 * @param {(data: any) => void} onData  Empfängt jeden erfolgreichen Abruf.
 */
export default function usePollingWhenVisible(url, intervalMs, onData) {
    // onData in einem Ref halten, damit der Effekt nicht bei jedem Render
    // neu anläuft - sonst würde das Polling mit jeder Zustandsänderung der
    // aufrufenden Komponente neu gestartet.
    const onDataRef = useRef(onData);
    onDataRef.current = onData;

    useEffect(() => {
        if (!url) {
            return;
        }
        let cancelled = false;
        let timer = null;

        const tick = async () => {
            try {
                const {data} = await axios.get(url);
                if (!cancelled) {
                    onDataRef.current(data);
                }
            } catch (e) {
                // Still: Ein Ausfall darf weder das Polling stoppen noch
                // die Konsole fluten. Beim nächsten Tick wird erneut versucht.
            }
            if (!cancelled) {
                timer = setTimeout(tick, intervalMs);
            }
        };

        const onVisibility = () => {
            if (document.visibilityState !== "visible") {
                // Hintergrund: Poll anhalten und Timer aufräumen.
                if (timer) {
                    clearTimeout(timer);
                    timer = null;
                }
                return;
            }
            // Rückkehr in den Vordergrund: laufenden Timer abbrechen und
            // sofort einmal frisch abfragen - nach längerer Pause will der
            // Nutzer nicht auf den nächsten Tick warten.
            if (timer) {
                clearTimeout(timer);
            }
            tick();
        };

        // Erster Poll erst nach einem vollen Intervall: der Initialwert
        // kommt ohnehin von Inertia, ein sofortiger Abruf bei jedem
        // Seitenwechsel würde das Portal ohne Not belasten.
        if (document.visibilityState === "visible") {
            timer = setTimeout(tick, intervalMs);
        }
        document.addEventListener("visibilitychange", onVisibility);

        return () => {
            cancelled = true;
            if (timer) {
                clearTimeout(timer);
            }
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [url, intervalMs]);
}
